import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { ClientOverview, Contact, Provider, UpdateClientOverviewRequest } from '../types';
import ContactManager from './ContactManager';

interface ClientOverviewEditorProps {
  isOpen: boolean;
  onClose: () => void;
  clientOverview: ClientOverview;
  onSave: (updatedOverview: ClientOverview) => void;
}

const ClientOverviewEditor: React.FC<ClientOverviewEditorProps> = ({
  isOpen,
  onClose,
  clientOverview,
  onSave
}) => {
  const [formData, setFormData] = useState({
    clientName: '',
    clientCountry: '',
    clientLogo: '',
    providerId: '',
    openAITools: '',
    useCase: '',
    startDate: '',
    currentPhase: '',
    projectSize: '',
    ultimateGoal: '',
  });

  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showContactManager, setShowContactManager] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        clientName: clientOverview.client.name,
        clientCountry: clientOverview.client.country,
        clientLogo: clientOverview.client.logo || '',
        providerId: clientOverview.provider.id,
        openAITools: clientOverview.openAITools,
        useCase: clientOverview.useCase,
        startDate: clientOverview.startDate,
        currentPhase: clientOverview.currentPhase,
        projectSize: clientOverview.projectScale.projectSize,
        ultimateGoal: clientOverview.ultimateGoal || '',
      });

      setSelectedContacts([...clientOverview.contacts]);
      setError(null);
    }
  }, [isOpen, clientOverview]);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const providersData = await apiService.getProviders();
        setProviders(providersData);
      } catch (err) {
        console.error('Failed to fetch providers:', err);
        setError('Failed to load providers');
      }
    };

    if (isOpen) {
      fetchProviders();
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check if a provider is selected
      if (!formData.providerId) {
        setError('Please select a provider before saving.');
        setLoading(false);
        return;
      }

      // Determine which contacts are new, updated, or deleted
      const originalContactIds = new Set(clientOverview.contacts.map(c => c.id));
      const selectedContactIds = new Set(selectedContacts.map(c => c.id));

      const contactsToAdd = selectedContacts.filter(c => !originalContactIds.has(c.id));
      const contactsToDelete = clientOverview.contacts.filter(c => !selectedContactIds.has(c.id));

      const updateRequest: UpdateClientOverviewRequest = {
        client: {
          name: formData.clientName,
          country: formData.clientCountry,
          logo: formData.clientLogo,
        },
        providerId: formData.providerId,
        openAITools: formData.openAITools,
        useCase: formData.useCase,
        startDate: formData.startDate,
        currentPhase: formData.currentPhase,
        projectScale: {
          projectSize: formData.projectSize,
        },
        ultimateGoal: formData.ultimateGoal,
        contacts: {
          toAdd: contactsToAdd.map(contact => ({
            name: contact.name,
            role: contact.role,
            email: contact.email,
            phone: contact.phone,
            providerId: contact.providerId || formData.providerId,
          })),
          toUpdate: [], // We don't update contacts here anymore, they're managed separately
          toDelete: contactsToDelete.map(contact => contact.id),
        }
      };

      const updatedOverview = await apiService.updateClientOverview(
        clientOverview.client.id,
        updateRequest
      );

      onSave(updatedOverview);
      onClose();
    } catch (err) {
      console.error('Failed to save client overview:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: '1px solid #e1e5e9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111' }}>
            Edit Client Overview
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 28px', maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <div style={{ 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '20px',
              border: '1px solid #f5c6cb'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Client Name
              </label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) => handleInputChange('clientName', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Country
              </label>
              <input
                type="text"
                value={formData.clientCountry}
                onChange={(e) => handleInputChange('clientCountry', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Logo URL
              </label>
              <input
                type="url"
                value={formData.clientLogo}
                onChange={(e) => handleInputChange('clientLogo', e.target.value)}
                placeholder="https://example.com/logo.png"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Provider
              </label>
              <select
                value={formData.providerId}
                onChange={(e) => handleInputChange('providerId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  backgroundColor: 'white',
                }}
              >
                <option value="">Select a provider</option>
                {providers.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                OpenAI Tools
              </label>
              <input
                type="text"
                value={formData.openAITools}
                onChange={(e) => handleInputChange('openAITools', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Use Case
              </label>
              <textarea
                value={formData.useCase}
                onChange={(e) => handleInputChange('useCase', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Start Date
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Current Phase
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={formData.currentPhase}
                  onChange={(e) => handleInputChange('currentPhase', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e1e5e9',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif',
                    backgroundColor: 'white',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                  }}
                >
                  <option value="" disabled>Select phase</option>
                  <option value="in production">🟢 In Production</option>
                  <option value="testing">🟡 Testing</option>
                  <option value="development">🔵 Development</option>
                  <option value="planning">⚪ Planning</option>
                  <option value="discontinued">🔴 Discontinued</option>
                </select>
                {/* Custom selected value with colored circle */}
                {formData.currentPhase && (
                  <div style={{
                    position: 'absolute',
                    left: '18px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {formData.currentPhase === 'in production' && (
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: '#22c55e', marginRight: 8 }} />
                    )}
                    {formData.currentPhase === 'testing' && (
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: '#facc15', marginRight: 8 }} />
                    )}
                    {formData.currentPhase === 'development' && (
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: '#3b82f6', marginRight: 8 }} />
                    )}
                    {formData.currentPhase === 'planning' && (
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: '#d1d5db', marginRight: 8 }} />
                    )}
                    {formData.currentPhase === 'discontinued' && (
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: '#ef4444', marginRight: 8 }} />
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Project Size (comma separated list)
              </label>
              <input
                type="text"
                value={formData.projectSize}
                onChange={(e) => handleInputChange('projectSize', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                Ultimate Goal
              </label>
              <input
                type="text"
                value={formData.ultimateGoal}
                onChange={(e) => handleInputChange('ultimateGoal', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>
          </div>

          {/* Contacts Section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                Contacts
              </h3>
              <button
                onClick={() => setShowContactManager(true)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                }}
              >
                Manage Contacts
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {selectedContacts.map((contact) => (
                <div
                  key={contact.id}
                  style={{
                    border: '1px solid #e1e5e9',
                    borderRadius: '8px',
                    padding: '12px',
                    backgroundColor: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                      {contact.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {contact.role}
                    </div>
                    {(contact.email || contact.phone) && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {contact.email && <span>📧 {contact.email}</span>}
                        {contact.email && contact.phone && <span> • </span>}
                        {contact.phone && <span>📞 {contact.phone}</span>}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedContacts(prev => prev.filter(c => c.id !== contact.id))}
                    style={{
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                    title="Remove contact from this client (contact will not be deleted)"
                  >
                    Remove
                  </button>
                </div>
              ))}

              {selectedContacts.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px', 
                  color: '#6b7280',
                  fontSize: '14px',
                  border: '1px dashed #d1d5db',
                  borderRadius: '8px',
                }}>
                  No contacts selected. Click "Manage Contacts" to add contacts.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 28px',
          borderTop: '1px solid #e1e5e9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div></div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid #e1e5e9',
                backgroundColor: 'white',
                color: '#374151',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Contact Manager Dialog */}
      <ContactManager
        isOpen={showContactManager}
        onClose={() => setShowContactManager(false)}
        providerId={formData.providerId}
        selectedContacts={selectedContacts}
        onContactsChange={setSelectedContacts}
      />
    </div>
  );
};

export default ClientOverviewEditor; 